import React, { useState, useCallback, useEffect } from 'react';
import { Input, Select, Space } from 'antd';
import {
  countryPhoneCodes,
  CountryPhoneCode,
  defaultCountry,
} from '../../const/country-phone-codes.const';
import Flag from 'react-flagkit';
import { phoneService } from '../../services/phone.service';

export interface PhoneNumberInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  allowClear?: boolean;
  showSearch?: boolean;
  defaultCountryCode?: string;
  countryWidthPercent?: number; // 0-100, remaining goes to number input
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Enter phone number',
  disabled = false,
  size = 'middle',
  style,
  className,
  allowClear = true,
  showSearch = true,
  defaultCountryCode = 'AE',
  countryWidthPercent = 30,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneCode>(
    countryPhoneCodes.find((country) => country.code === defaultCountryCode) || defaultCountry
  );

  // Parse the current value to extract country code and phone number
  const parsePhoneValue = useCallback(
    (phoneValue: string) => {
      if (!phoneValue) return { country: selectedCountry, number: '' };

      // First try to parse as E.164 format (e.g., +923034234895)
      if (phoneValue.startsWith('+')) {
        const phoneInfo = phoneService.getPhoneNumberInfo(phoneValue);
        if (phoneInfo && phoneInfo.country) {
          const matchingCountry = countryPhoneCodes.find(
            (country) => country.code === phoneInfo.country
          );
          if (matchingCountry) {
            return { country: matchingCountry, number: phoneInfo.nationalNumber };
          }
        }
      }

      // Fallback: Check if the value starts with a country code
      const matchingCountry = countryPhoneCodes.find((country) =>
        phoneValue.startsWith(country.phoneCode)
      );

      if (matchingCountry) {
        const number = phoneValue.substring(matchingCountry.phoneCode.length);
        return { country: matchingCountry, number };
      }

      return { country: selectedCountry, number: phoneValue };
    },
    [selectedCountry]
  );

  const { country, number } = parsePhoneValue(value);

  // Update selected country when value changes externally (e.g., from backend data)
  useEffect(() => {
    if (value && value.startsWith('+')) {
      const phoneInfo = phoneService.getPhoneNumberInfo(value);
      if (phoneInfo && phoneInfo.country) {
        const matchingCountry = countryPhoneCodes.find((c) => c.code === phoneInfo.country);
        if (matchingCountry && matchingCountry.code !== selectedCountry.code) {
          setSelectedCountry(matchingCountry);
        }
      }
    }
  }, [value, selectedCountry.code]);

  const handleCountryChange = useCallback(
    (countryCode: string) => {
      const newCountry = countryPhoneCodes.find((c) => c.code === countryCode) || defaultCountry;
      setSelectedCountry(newCountry);

      if (onChange) {
        const fullNumber = number ? `${newCountry.phoneCode}${number}` : '';
        onChange(fullNumber);
      }
    },
    [number, onChange]
  );

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Only allow numbers
      const cleanValue = inputValue.replace(/[^\d]/g, '');

      if (onChange) {
        const fullNumber = cleanValue ? `${selectedCountry.phoneCode}${cleanValue}` : '';
        onChange(fullNumber);
      }
    },
    [selectedCountry, onChange]
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const countryOptions = countryPhoneCodes.map((country) => ({
    value: country.code,
    label: (
      <Space>
        <Flag country={country.code} size={20} />
        <span>{country.alpha3 || country.code}</span>
        <span style={{ color: '#999', fontSize: '12px' }}>{country.phoneCode}</span>
      </Space>
    ),
  }));

  return (
    <Input.Group compact style={style} className={className}>
      <Select
        value={country.code}
        onChange={handleCountryChange}
        disabled={disabled}
        size={size}
        style={{ width: `${Math.max(0, Math.min(100, countryWidthPercent))}%` }}
        showSearch={showSearch}
        filterOption={(input, option) => {
          const country = countryPhoneCodes.find((c) => c.code === option?.value);
          if (!country) return false;
          return (
            country.name.toLowerCase().includes(input.toLowerCase()) ||
            country.phoneCode.includes(input) ||
            country.code.toLowerCase().includes(input.toLowerCase()) ||
            Boolean(country.alpha3 && country.alpha3.toLowerCase().includes(input.toLowerCase()))
          );
        }}
        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
      >
        {countryOptions.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
      <Input
        value={number}
        onChange={handleNumberChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        style={{ width: `${100 - Math.max(0, Math.min(100, countryWidthPercent))}%` }}
        allowClear={allowClear}
        onKeyDown={(e) => {
          // Allow: backspace, delete, tab, escape, enter, and numbers
          if (
            [8, 9, 27, 13, 46].includes(e.keyCode) ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey) ||
            (e.keyCode === 67 && e.ctrlKey) ||
            (e.keyCode === 86 && e.ctrlKey) ||
            (e.keyCode === 88 && e.ctrlKey) ||
            // Allow numbers
            (e.keyCode >= 48 && e.keyCode <= 57) ||
            (e.keyCode >= 96 && e.keyCode <= 105)
          ) {
            return;
          }
          e.preventDefault();
        }}
      />
    </Input.Group>
  );
};

export default PhoneNumberInput;
